import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import { PUBLIC_BASE } from './config';
import PublicLayout from '../../components/public/PublicLayout';
import { Upload } from 'lucide-react';

const ALBUM_TYPE_MAP = { 1: '싱글', 2: 'EP', 3: '정규', 4: '컴필레이션' };

export default function PublicReviewModifyPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { me } = useSelector(authSelector.selectAuthState);
  const currentPage = parseInt(new URLSearchParams(location.search).get('page')) || 1;

  const isAdmin = me?.userType === 'client' && me?.adminYn === 'Y';

  // 앨범 정보
  const [albumName, setAlbumName] = useState('');
  const [clientName, setClientName] = useState('');
  const [artistName, setArtistName] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [albumTypeId, setAlbumTypeId] = useState('');
  const [physicalAlbumYn, setPhysicalAlbumYn] = useState('N');
  const [riakId, setRiakId] = useState('');
  const [riakProducerName, setRiakProducerName] = useState('');

  // 곡 정보
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [currentSongIdx, setCurrentSongIdx] = useState(null);
  const [tempLyrics, setTempLyrics] = useState('');
  const [tempLyricsTranslation, setTempLyricsTranslation] = useState('');
  const [hasTranslation, setHasTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [uploading, setUploading] = useState({});

  // 심의결과 모달 (관리자)
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [broadcasters, setBroadcasters] = useState([]);
  const [results, setResults] = useState({});
  const [currentDetailId, setCurrentDetailId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/public/reviewApply/${id}`);
        const { album, songs: songData } = res.data;

        setAlbumName(album?.albumName || '');
        setClientName(album?.clientName || '');
        setArtistName(album?.artistName || '');
        setReleaseDate(album?.releaseDate || '');
        setAlbumTypeId(album?.albumTypeId || '');
        setPhysicalAlbumYn(album?.physicalAlbumYn || 'N');
        setRiakId(album?.riakId || '');
        setRiakProducerName(album?.riakProducerName || '');

        setSongs((songData || []).map((s) => ({
          memberReviewSongId: s.memberReviewSongId || s.id,
          reviewApplyDetailId: s.reviewApplyDetailId,
          titleYn: s.titleYn || 'N',
          track: s.track,
          nameKr: s.nameKr || '',
          writerKr: s.writerKr || '',
          composerKr: s.composerKr || '',
          arrangerKr: s.arrangerKr || '',
          featuring: s.featuring || '',
          featuringLatestRelease: s.featuringLatestRelease || '',
          performance: s.performance || '',
          lyrics: s.lyrics || '',
          lyricsTranslation: s.lyricsTranslation || '',
          woneumYn: s.woneumYn === 'Y',
          fileUrl: s.fileUrl || null,
          reviewApplyResults: s.reviewApplyResults || [],
        })));
      } catch (e) {
        console.error(e);
        alert('데이터 불러오기 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!lyricsModalOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') closeLyricsModal(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lyricsModalOpen]);

  const updateSong = (idx, field, value) => {
    setSongs((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const updateWoneumYn = (idx, checked) => {
    const count = songs.filter((s) => s.woneumYn).length;
    if (checked && count >= 3) {
      alert('원음방송은 최대 3곡까지 선택할 수 있습니다.');
      return;
    }
    updateSong(idx, 'woneumYn', checked);
  };

  const handleFileUpload = async (idx, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [idx]: true }));
    try {
      const formData = new FormData();
      formData.append('audio', file);
      const res = await axios.post('/api/public/reviewApply/upload-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateSong(idx, 'fileUrl', res.data.fileUrl);
    } catch (e) {
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setUploading((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const openLyricsModal = (idx) => {
    setCurrentSongIdx(idx);
    setTempLyrics(songs[idx]?.lyrics || '');
    const trans = songs[idx]?.lyricsTranslation || '';
    setTempLyricsTranslation(trans);
    setHasTranslation(!!trans);
    setLyricsModalOpen(true);
  };
  const closeLyricsModal = () => {
    setLyricsModalOpen(false); setCurrentSongIdx(null);
    setTempLyrics(''); setTempLyricsTranslation(''); setHasTranslation(false); setTranslating(false);
  };
  const autoTranslate = async () => {
    if (!tempLyrics.trim()) return;
    setHasTranslation(true);
    setTranslating(true);
    try {
      const res = await axios.post('/api/public/reviewApply/translate-lyrics', { lyrics: tempLyrics });
      setTempLyricsTranslation(res.data.translation || '');
    } catch (e) {
      alert('번역 중 오류가 발생했습니다.');
    } finally {
      setTranslating(false);
    }
  };
  const saveLyricsModal = () => {
    if (currentSongIdx !== null) {
      updateSong(currentSongIdx, 'lyrics', tempLyrics);
      updateSong(currentSongIdx, 'lyricsTranslation', hasTranslation ? tempLyricsTranslation : '');
    }
    closeLyricsModal();
  };

  const openResultModal = async (detailId) => {
    try {
      const res = await axios.get(`/api/public/reviewApply/${detailId}/result`);
      setBroadcasters(res.data.map((r) => ({ id: r.broadcasterId, name: r.broadcasterName })));
      const init = {};
      res.data.forEach((r) => { init[r.broadcasterId] = { status: r.status, remark: r.remark }; });
      setResults(init);
      setCurrentDetailId(detailId);
      setResultModalOpen(true);
    } catch (e) {
      alert('심의 결과 불러오기 실패');
    }
  };

  const saveResults = async () => {
    try {
      await axios.put(`/api/public/reviewApply/${currentDetailId}/result`, {
        results: Object.entries(results).map(([bid, r]) => ({
          broadcasterId: Number(bid), status: r.status, remark: r.remark || '',
        })),
      });
      alert('심의결과가 저장되었습니다.');
      setResultModalOpen(false);
    } catch (e) {
      alert(e.response?.data?.message || '저장 실패');
    }
  };

  const validationErrors = useMemo(() => {
    const errors = [];
    if (!albumName.trim()) errors.push('앨범명');
    if (!clientName.trim()) errors.push('권리사명');
    if (!artistName.trim()) errors.push('아티스트명');
    if (!releaseDate) errors.push('발매일');
    if (!albumTypeId) errors.push('앨범 타입');
    if (!songs.length) { errors.push('곡 정보 없음'); return errors; }
    for (let i = 0; i < songs.length; i++) {
      const s = songs[i];
      const t = `${i + 1}번 곡`;
      if (!s.nameKr?.trim()) errors.push(`${t}: 곡명`);
      if (!s.composerKr?.trim()) errors.push(`${t}: 작곡`);
      if (!s.performance?.trim()) errors.push(`${t}: 실연(연주자명/악기명)`);
      if (!(s.nameKr || '').includes('Inst.') && !s.lyrics?.trim()) errors.push(`${t}: 가사`);
    }
    const bcCount = songs.filter((s) => s.woneumYn).length;
    if (bcCount === 0) errors.push('원음방송 최소 1곡 선택');
    if (bcCount > 3) errors.push('원음방송 최대 3곡');
    return errors;
  }, [albumName, clientName, artistName, releaseDate, albumTypeId, songs]);

  const canSubmit = validationErrors.length === 0;

  const onSubmit = async () => {
    if (!canSubmit) { alert('필수 항목을 확인해 주세요.\n\n누락: ' + validationErrors.join(', ')); return; }
    try {
      const payload = {
        albumName: albumName.trim(),
        clientName: clientName.trim(),
        artistName: artistName.trim(),
        releaseDate,
        albumTypeId: Number(albumTypeId),
        physicalAlbumYn,
        riakId: riakId.trim(),
        riakProducerName: riakProducerName.trim(),
        songs: songs.map((s) => ({
          memberReviewSongId: s.memberReviewSongId,
          titleYn: s.titleYn,
          track: s.track,
          nameKr: s.nameKr?.trim(),
          writerKr: s.writerKr?.trim(),
          composerKr: s.composerKr?.trim(),
          arrangerKr: s.arrangerKr?.trim(),
          featuring: s.featuring?.trim(),
          featuringLatestRelease: s.featuringLatestRelease?.trim(),
          performance: s.performance?.trim(),
          lyrics: s.lyrics?.trim(),
          lyricsTranslation: s.lyricsTranslation?.trim() || null,
          woneumYn: s.woneumYn || false,
          fileUrl: s.fileUrl || null,
        })),
      };
      await axios.put(`/api/public/reviewApply/${id}`, payload);
      alert('신청서가 수정되었습니다.');
      navigate(`${PUBLIC_BASE}/review/${id}?page=${currentPage}`);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || '수정 중 오류가 발생했습니다.');
    }
  };

  if (loading) return (
    <PublicLayout>
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" />
        <span className="text-sm text-slate-500">불러오는 중...</span>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <h1 className="text-xl sm:text-2xl font-bold text-white text-center mb-8">신청서 수정</h1>

        <div className="space-y-6 max-w-3xl mx-auto">
          {/* 앨범 정보 */}
          <div className="grid grid-cols-1 gap-4">
            <InputField label="앨범명" required value={albumName} onChange={(e) => setAlbumName(e.target.value)} />
            <InputField label="권리사명" required value={clientName} onChange={(e) => setClientName(e.target.value)} />
            <InputField label="아티스트명" required value={artistName} onChange={(e) => setArtistName(e.target.value)} />
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                발매일 <span className="text-red-500">*</span>
              </label>
              <input type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                앨범 타입 <span className="text-red-500">*</span>
              </label>
              <select value={albumTypeId} onChange={(e) => setAlbumTypeId(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent">
                <option value="">선택</option>
                {Object.entries(ALBUM_TYPE_MAP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* 앨범 추가 입력 */}
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-4 bg-[#182334]">
            <InputField label="RIAK 아이디" value={riakId} onChange={(e) => setRiakId(e.target.value)} placeholder="예) pink" />
            <InputField label="RIAK 제작사명" value={riakProducerName} onChange={(e) => setRiakProducerName(e.target.value)} placeholder="예) 핑크페이퍼" />
          </div>

          {/* 구분선 */}
          <div className="flex items-center my-10">
            <div className="flex-grow border-t border-white/[0.08]" />
            <span className="px-3 text-slate-300 font-bold">곡 정보</span>
            <div className="flex-grow border-t border-white/[0.08]" />
          </div>

          {/* 곡 리스트 */}
          <div className="border border-white/[0.08] rounded-2xl p-4 space-y-6 bg-white/[0.03]">
            {songs.map((s, idx) => (
              <div key={idx} className="rounded-xl border border-white/[0.08] p-4 space-y-3 bg-[#182334]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                      타이틀YN <span className="text-red-500">*</span>
                    </label>
                    <select value={s.titleYn || 'N'} onChange={(e) => updateSong(idx, 'titleYn', e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent">
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </select>
                  </div>
                  <InputField label="트랙" required value={s.track || ''} onChange={(e) => updateSong(idx, 'track', e.target.value)} />
                  <InputField label="곡명" required value={s.nameKr} onChange={(e) => updateSong(idx, 'nameKr', e.target.value)} />
                  <InputField label="작사" value={s.writerKr} onChange={(e) => updateSong(idx, 'writerKr', e.target.value)} />
                  <InputField label="작곡" required value={s.composerKr} onChange={(e) => updateSong(idx, 'composerKr', e.target.value)} />
                  <InputField label="편곡" value={s.arrangerKr} onChange={(e) => updateSong(idx, 'arrangerKr', e.target.value)} />
                </div>

                {/* 음원 파일 */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">음원 파일</label>
                  {s.fileUrl ? (
                    <div className="space-y-2">
                      <audio controls src={s.fileUrl} className="w-full" />
                      <button onClick={() => updateSong(idx, 'fileUrl', null)} className="text-xs text-rose-400 hover:text-rose-300">파일 변경</button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/[0.15] rounded-xl cursor-pointer hover:bg-white/5 transition-colors">
                      {uploading[idx] ? (
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <div className="w-4 h-4 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
                          업로드 중...
                        </div>
                      ) : (
                        <>
                          <Upload size={16} className="text-slate-500" />
                          <span className="text-sm text-slate-400">음원 파일 선택 (MP3, WAV, FLAC)</span>
                        </>
                      )}
                      <input type="file" accept="audio/*" className="hidden" onChange={(e) => handleFileUpload(idx, e.target.files[0])} />
                    </label>
                  )}
                </div>

                <InputField label="피처링" value={s.featuring} onChange={(e) => updateSong(idx, 'featuring', e.target.value)} />
                <InputField label="피처링 아티스트의 최근 음원" value={s.featuringLatestRelease} onChange={(e) => updateSong(idx, 'featuringLatestRelease', e.target.value)} />
                <InputField label="실연(연주자명/악기명)" required value={s.performance} onChange={(e) => updateSong(idx, 'performance', e.target.value)} />

                {!(s.nameKr || '').includes('Inst.') && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                      가사 <span className="text-red-500">*</span>
                    </label>
                    <button
                      onClick={() => openLyricsModal(idx)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                        s.lyrics ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30'
                      }`}>
                      {s.lyrics ? '가사 수정' : '가사 입력'}
                    </button>
                  </div>
                )}

                {/* 원음방송 */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={s.woneumYn} onChange={(e) => updateWoneumYn(idx, e.target.checked)} className="peer hidden" />
                  <span className="w-6 h-6 flex items-center justify-center border border-white/[0.08] rounded-full peer-checked:bg-sky-500 peer-checked:border-sky-500 text-white text-sm">✓</span>
                  <span className="text-sm font-semibold text-slate-200">원음방송 선택 (최대 3곡) <span className="text-red-500">*</span></span>
                </label>

                {/* 관리자: 심의결과 입력 */}
                {isAdmin && s.reviewApplyDetailId && (
                  <div className="mt-3">
                    <button onClick={() => openResultModal(s.reviewApplyDetailId)}
                      className="w-full px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors">
                      심의결과 입력
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 버튼 */}
          <div className="flex flex-wrap justify-end items-center gap-2 mt-6">
            <button onClick={onSubmit}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                canSubmit ? 'bg-sky-600 text-white hover:bg-sky-500' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
              }`}>
              수정완료
            </button>
            <button onClick={() => navigate(`${PUBLIC_BASE}/review?page=${currentPage}`)}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">
              목록
            </button>
          </div>
        </div>
      </div>

      {/* 가사 모달 */}
      {lyricsModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50" onClick={closeLyricsModal}>
          <div className="bg-[#182334] rounded-2xl p-6 w-3/4 h-3/4 flex flex-col shadow-xl border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-white mb-2">가사 입력</h2>
            <p className="text-sm text-rose-400 mb-2">※ 영문 가사의 경우 <b>의역이 필수</b>입니다. ex) I want you only (난 너만 있으면 돼)</p>
            <textarea
              value={tempLyrics}
              onChange={(e) => setTempLyrics(e.target.value)}
              className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none ${hasTranslation ? 'flex-none h-2/5' : 'flex-1'}`}
              placeholder="곡의 전체 가사를 입력해 주세요."
            />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <input type="checkbox" id="hasTranslationCheck" checked={hasTranslation} onChange={(e) => setHasTranslation(e.target.checked)} className="w-4 h-4 accent-sky-400" />
              <label htmlFor="hasTranslationCheck" className="text-sm text-slate-300 cursor-pointer">한글번역 포함</label>
              <button onClick={autoTranslate} disabled={translating || !tempLyrics.trim()}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {translating ? '번역 중...' : '✦ 자동번역'}
              </button>
            </div>
            {hasTranslation && (
              <textarea
                value={translating ? '번역 중...' : tempLyricsTranslation}
                onChange={(e) => setTempLyricsTranslation(e.target.value)}
                readOnly={translating}
                className="flex-1 w-full mt-2 px-3 py-2 rounded-xl border border-sky-500/30 bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none"
                placeholder="한글번역 가사를 입력해 주세요."
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={closeLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors">취소</button>
              <button onClick={saveLyricsModal} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors">저장</button>
            </div>
          </div>
        </div>
      )}

      {/* 심의결과 모달 (관리자) */}
      {resultModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60]" onClick={() => setResultModalOpen(false)}>
          <div className="bg-[#182334] rounded-2xl p-6 w-full max-w-lg mx-4 border border-white/[0.08]" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-center mb-4 text-white">심의결과 입력</h2>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setResults((p) => { const n = {}; Object.keys(p).forEach((k) => { n[k] = { ...p[k], status: 'Y' }; }); return n; })}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/20 text-sky-400 hover:bg-sky-500/30">전체 통과</button>
              <button onClick={() => setResults((p) => { const n = {}; Object.keys(p).forEach((k) => { n[k] = { ...p[k], status: 'N' }; }); return n; })}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 text-rose-400 hover:bg-rose-500/30">전체 거절</button>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {broadcasters.map((b) => (
                <div key={b.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border border-white/[0.08] p-3 rounded-xl">
                  <span className="font-bold text-white text-sm">📻 {b.name}</span>
                  <div className="flex flex-col sm:flex-row gap-2 flex-1 justify-end items-center">
                    {['WAITING', 'Y', 'N'].map((st) => (
                      <button key={st} onClick={() => setResults((p) => ({ ...p, [b.id]: { ...p[b.id], status: st } }))}
                        className={`px-3 py-1 rounded-xl text-xs font-bold transition-colors ${
                          results[b.id]?.status === st
                            ? st === 'Y' ? 'bg-sky-500 text-white' : st === 'N' ? 'bg-red-500 text-white' : 'bg-slate-500 text-white'
                            : 'bg-white/5 text-slate-400 hover:bg-white/10'
                        }`}>
                        {st === 'Y' ? '통과' : st === 'N' ? '거절' : '심의중'}
                      </button>
                    ))}
                    <input type="text" value={results[b.id]?.remark || ''} placeholder="사유"
                      onChange={(e) => setResults((p) => ({ ...p, [b.id]: { ...p[b.id], remark: e.target.value } }))}
                      className="w-32 sm:w-40 px-2 py-1 text-sm rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 focus:outline-none" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={saveResults} className="px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-500">저장</button>
              <button onClick={() => setResultModalOpen(false)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600">닫기</button>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

function ROField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      <input type="text" value={typeof value === 'string' || typeof value === 'number' ? value : ''} readOnly
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/5 text-slate-400 text-sm focus:outline-none" />
    </div>
  );
}

function InputField({ label, value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input type="text" value={value} onChange={onChange} placeholder={placeholder}
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent" />
    </div>
  );
}
